import { useDeletePresetMutationApi } from '@aibees/shared/api';
import { useCreateOrUpdatePresetMutation } from '@aibees/shared/api/preset/use-create-or-update-preset-mutation';
import { DialogActions, DialogTitle } from '@aibees/shared/components';
import { TemplateManagement } from '@aibees/shared/components/template-management';
import { Maybe, ParsedPreset } from '@aibees/shared/definition';
import { OperationMode } from '@aibees/shared/features/template-management-dialog';
import useTransferListStore from '@aibees/shared/features/transfer-list/store/transfer-list-store';
import { useBoolean, useNotify } from '@aibees/shared/hooks';
import { baseQueryClient } from '@aibees/shared/http/base-query-client';
import { classnames } from '@aibees/shared/utils';
import { Dialog, DialogContent, Fade, Typography } from '@mui/material';
import { random } from 'lodash-es';
import { memo, useMemo, useState } from 'react';
import {
  TEMPLATE_LABEL_BOX_WIDTH,
  TEMPLATE_LABEL_BOX_WIDTH_WITH_VISIBILITY_ICON,
} from '../../constant';
import { useAdvanceTableContext } from '../../context';
import { useCreateDefaultTableLayoutModifierTemplate } from '../../hooks/use-create-default-table-layout-modifier-template';
import { TableLayoutModifierCreateDialog } from '../table-layout-modifier-create-dialog';
import { useTableLayoutTemplateContext } from '../table-layout-modifier-dialog/context';
import { useCompareInternalTemplateChanges } from '../table-layout-modifier-dialog/hooks/use-compare-internal-template-changes';
import {
  TableLayoutTemplate,
  TableLayoutTemplateItem,
} from '../table-layout-modifier-dialog/types';
import { obtainTableModifierDataFromConfig } from '../table-layout-modifier/util';
import * as Styled from './styles';
import * as SX from './sx';
import type { TableLayoutModifierDialogV2Props as Props } from './types';

export const TableLayoutModifierDialogV2 = memo(
  ({ className, element, config: createDialogConfig }: Props) => {
    useCreateDefaultTableLayoutModifierTemplate();
    const {
      tableLayoutModifierDialogState,
      id,
      setAppliedId,
      setId,
      selectedPreset,
      templates,
      setOperationMode,
      setTemplateEditItem,
      templateEditItem,
      setTemplateName,
    } = useTableLayoutTemplateContext();
    const createDialogState = useBoolean(false);
    const { config } = useAdvanceTableContext();

    const transferListConfig = obtainTableModifierDataFromConfig(config);
    const [internalTemplateEditItem, setInternalTemplateEditItem] =
      useState<Maybe<TableLayoutTemplateItem>>(undefined);
    const [initialTemplatesLength, setInitialTemplatesLength] = useState<
      number | undefined
    >(undefined);
    const { setExcluded, setIncluded } = useTransferListStore();
    const notify = useNotify();
    const submitLoading = useBoolean();
    const { mutateAsync } = useCreateOrUpdatePresetMutation<string>(
      'SELECTED_'.concat(config.columnModifierPresetType!),
      false
    );
    const hasTemplatesLengthChanged = useMemo(() => {
      return (
        initialTemplatesLength !== undefined &&
        templates.length !== initialTemplatesLength
      );
    }, [initialTemplatesLength, templates.length]);
    const hasInternalTemplateChanges = useCompareInternalTemplateChanges(
      internalTemplateEditItem
    );
    const deleteApi = useDeletePresetMutationApi(selectedPreset?.id);

    const handleClose = () => {
      tableLayoutModifierDialogState.onFalse();
      setInitialTemplatesLength(undefined);
    };

    const handleCreate = () => {
      createDialogState.onTrue();
      tableLayoutModifierDialogState.onFalse();
    };

    const handleSubmit = async () => {
      tableLayoutModifierDialogState.onTrue();
      setTemplateEditItem(undefined);
      setInternalTemplateEditItem(undefined);
      setInitialTemplatesLength(undefined);
      try {
        submitLoading.onTrue();

        if (id) {
          const selectedTemplateName =
            templates.find((t) => t.id === id)?.name ?? '';
          await mutateAsync(
            id,
            'SELECTED_'.concat(config.columnModifierPresetType!),
            selectedTemplateName
          );
          notify.complete(
            'Template Applied',
            'Your selected column template has been applied to the result table.',
            random(1, 1000000),
            {
              icon: 'view-week-outline',
            }
          );
          setAppliedId(undefined);
        } else if (selectedPreset?.id) {
          deleteApi.mutateAsync(null);
          baseQueryClient.invalidateQueries([
            'useBrowsePresetApi',
            'SELECTED_'.concat(config.columnModifierPresetType!),
            'SELECTED_'.concat(config.columnModifierPresetType!),
            {},
          ]);
          setId(undefined);
          setAppliedId(undefined);
          notify.complete(
            'Template Applied',
            'Your selected column template has been applied to the result table.',
            random(1, 1000000),
            {
              icon: 'view-week-outline',
            }
          );
        } else {
          setId(undefined);
          setAppliedId(undefined);
          notify.complete(
            'Template Applied',
            'Your selected column template has been applied to the result table.',
            random(1, 1000000),
            {
              icon: 'view-week-outline',
            }
          );
        }
        handleClose();
      } catch (error) {
        tableLayoutModifierDialogState.onTrue();
        createDialogState.onTrue();
        notify.error('Failed to apply template to table');
        console.error('Error applying template:', error);
      } finally {
        submitLoading.onFalse();
      }
    };

    const handleEdit = (value: ParsedPreset<TableLayoutTemplate>) => {
      tableLayoutModifierDialogState.onFalse();
      createDialogState?.onTrue();
      setTemplateName(value.name);
      setOperationMode(OperationMode.EDIT);
      setTemplateEditItem(value);
      setInternalTemplateEditItem(value);
      setId(value.id);
      setExcluded(value.value.excluded);
      setIncluded(value.value.included);
    };

    const handleSelect = (value: ParsedPreset<TableLayoutTemplate>) => {
      setId(value.id);
    };

    const handleDelete = () => {
      setInitialTemplatesLength(templates.length);
    };
    return (
      <Styled.Wrapper
        className={classnames('TableLayoutModifierDialogV2', className)}
        sx={SX.root}
      >
        {element}
        <Dialog
          open={tableLayoutModifierDialogState.value}
          fullWidth
          maxWidth="xs"
          TransitionComponent={Fade}
          onClose={handleClose}
        >
          <DialogTitle
            title={
              <Typography color="gray.800" fontWeight={400} variant="body2">
                Edit Columns
              </Typography>
            }
            icon="view-week-outline"
            closeIconSize={16}
            closeIconColor="grey.700"
            onClose={handleClose}
          />

          <DialogContent>
            <TemplateManagement
              presetType={config.columnModifierPresetType!}
              templates={templates}
              placeholder="Search available templates"
              selectedPreset={selectedPreset}
              selectedTemplateId={id}
              deleteDialogTitle="Delete Column Template"
              deleteNotificationTitle="Column Template Removed"
              deleteNotificationDescription="The selected column template has been deleted from your workspace."
              deleteNotificationIcon="view-week-outline"
              labelWidth={TEMPLATE_LABEL_BOX_WIDTH}
              labelWidthWithVisibilityIcon={
                TEMPLATE_LABEL_BOX_WIDTH_WITH_VISIBILITY_ICON
              }
              onCreate={handleCreate}
              onSelect={handleSelect}
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          </DialogContent>

          <DialogActions
            submitTitle="Apply Changes"
            onClose={handleClose}
            onSubmit={handleSubmit}
            disabled={
              !hasInternalTemplateChanges &&
              !hasTemplatesLengthChanged &&
              id === selectedPreset?.value
            }
            loading={submitLoading.value}
          />
        </Dialog>

        <TableLayoutModifierCreateDialog
          state={createDialogState}
          config={createDialogConfig}
        />
      </Styled.Wrapper>
    );
  }
);

TableLayoutModifierDialogV2.displayName = 'TableLayoutModifierDialogV2';
